import { useCallback, useRef } from 'react'
import type { TerminalSocket } from '../transport/ws'

/**
 * THE SEAM BETWEEN THE TRANSCRIPT AND THE SOCKET — four refs that were reached into
 * from both effects, made into one explicit thing (M8 cut 1, 2026-09-03).
 *
 * TranscriptView runs two independent machines that must talk: the transcript effect
 * (HTTP reads, the window) and the headless socket effect (the pty link). They
 * communicated through four TranscriptView-scoped refs — `activity`, `armWatch`,
 * `unborn`, `watchArmed` — which is the "weld" the census named: neither effect owns
 * them, both mutate them, and nothing states the contract. Moving them here changes no
 * behaviour; it gives the contract a name and a place.
 *
 * Every comment below came with its ref, verbatim. They are measured history.
 */
export function usePaneWatch(args: { session: string; source: string }) {
  const { session, source } = args
  /*
   * The pane's own output is what tells this view to re-read. The socket effect points
   * activity into it, the transcript effect points it at the throttled re-read.
   * A ref, not state — firing per frame through setState would re-render the
   * whole conversation at the terminal's own rate.
   */
  const activity = useRef<(() => void) | null>(null)
  /*
   * THE FILE ANNOUNCES ITS OWN CHANGES — owner, 2026-08-24: Clean View 沒有更新,
   * 停留在歷史版本 (the browser held 6336 blocks while the file had 6366).
   *
   * The activity ping above rides the pane's output, which reaches this socket
   * only if its `tmux attach` actually landed — a flat 300ms wait on a login
   * shell that a loaded machine loses, silently. Before the fan-out diet the
   * account-wide pty bus masked that: anyone's bytes re-read this view. Scoping
   * the trigger to this socket removed the mask and exposed the dependency.
   *
   * So the trigger is now the transcript file itself: `claude-watch` in signal
   * mode makes the edge send `claude-touch` on every write to the file this
   * view renders, attached or not. Armed only once the first HTTP read has
   * named the file, and WITH file/have — an edge too old for signal mode
   * answers the same watch with deltas from `have`, and without those two
   * fields that answer would be the whole conversation.
   */
  const watchArmed = useRef<{ sock: TerminalSocket | null; file: string | null }>({ sock: null, file: null })
  /*
   * `unborn`: the server has said there is no transcript AT ALL (404) and nothing
   * has contradicted it since. A STATE, not a one-shot argument: the 404 often
   * lands before the headless socket is ready, so the arm it asks for has to
   * survive until the socket's own onOpen calls armWatch — which carries no
   * arguments and, as a flag, would have found nothing and armed nothing. That
   * was measured: a fresh agent's first reply never surfaced while the pane
   * showed it. Arming unborn is safe (an edge too old for signal mode has nothing
   * to dump) and is where it matters most; the edge watches the project directory
   * until the file is born.
   */
  const unborn = useRef(false)

  /**
   * Ask the machine to announce this file's writes. Reads the transcript side's
   * position through the getters rather than closing over it: the socket effect calls
   * this from onOpen, long after the render that created it.
   */
  const arm = useCallback((get: {
    socket: () => TerminalSocket | null
    file: () => string | null
    have: () => number
  }) => {
    const sock = get.socket()
    if (!sock?.ready) return
    const currentFile = get.file()
    if (currentFile === null && !unborn.current) return
    const key = currentFile ?? ''
    if (watchArmed.current.sock === sock && watchArmed.current.file === key) return
    watchArmed.current = { sock, file: key }
    sock.control({
      type: 'claude-watch',
      session,
      source,
      file: key,
      have: get.have(),
      signal: true,
      /*
       * ALSO STREAM THE MESSAGE IN FLIGHT, where the machine can. Opt-in and
       * literal-true: an edge that predates it ignores the field, and a CLI with
       * no live source of its own (claude) simply never pushes — its live block
       * is read from the pane instead.
       */
      live: true,
    })
    /*
     * useCallback with [session, source]: BOTH effects hold this across renders, and
     * an identity that changed every render would either be excluded from their dep
     * lists (a lie the linter would flag) or re-run them constantly. The refs are
     * stable by construction; these two strings are the only inputs that can change,
     * and when they do a re-arm is exactly right.
     */
  }, [session, source])

  return { activity, watchArmed, unborn, arm }
}
